import { LoadingSpinner, SectionCard, Tabs, TextHeader } from '@components';
import { AssetsService, UsersService } from '@services';
import { RootState, addToast, updateHardware } from '@stores';
import React, { FC, useEffect, useReducer, useRef, useState } from 'react';
import { Trash2 } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AssetCategory from '../../components/tags/AssetCategory';
import UserSelect from '../../components/users/UserSelect';
import HardwareTickets from './HardwareTickets';
import ProductInfo from './ProductInfo';

type Asset = {
  asset: any;
  data: {
    asset: any;
  };
};

type tag = {
  id: number;
  tag: string;
};

const AssetDetailContainer: FC<Asset> = ({ asset, data }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(true);
  const [tagLoading, setTagLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [tags, setTags] = useState<tag[]>();
  const tagInput = useRef<HTMLInputElement>(null);
  const [wiggleTag, setWiggleTag] = useState<number | null>(null);
  const [duplicateTagError, setDuplicateTagError] = useState<string | null>(
    null
  );

  const companyId = useSelector(
    (state: RootState) => state.companies.selectedCompany?.id
  );

  const [assetData, setAssetData] = useState(data.asset);

  const service = new UsersService();
  const assetService = new AssetsService();
  const dispatch = useDispatch();

  const [force, forceUpdate] = useReducer((x) => x + 1, 0);

  const tabs = [
    {
      nav: 'Productinfo',
      component: [
        <ProductInfo
          asset={data.asset}
          category={data.asset.ordPro_categorie}
        />,
      ],
    },
    {
      nav: 'Tickets',
      component: [<HardwareTickets assetId={data.asset.ordSer_id} />],
    },
  ];

  useEffect(() => {
    setTags(data.asset.tags);
  }, []);

  useEffect(() => {
    setLoading(true);
    setFormLoading(true);

    const fetchData = async () => {
      await fetchOptions();

      setFormLoading(false);
      setLoading(false);
    };

    fetchData();
  }, [data.asset]);

  const fetchOptions = async () => {
    const fetchedOptions = await service.getUserNames(companyId);
    setOptions(fetchedOptions);
    setAssetData(data.asset);
  };

  const handleSelect = async (value: string, label: string) => {
    const asset = await assetService.updateAssetUser(
      companyId,
      data.asset.ordSer_id,
      value,
      label.label
    );

    dispatch(
      updateHardware({
        id: data.asset.ordSer_id,
        updatedData: {
          ...data.asset,
          ordSer_user_id: value,
          ordSer_username: label.label,
        },
      })
    );

    dispatch(
      addToast({
        description: 'De gebruiker is gewijzigd.',
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleInputKeyPress = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      const newTag = inputValue.trim();

      // Check for duplicate tag
      const existingTag = tags.find((tag) => tag.tag === newTag);

      if (existingTag) {
        setWiggleTag(existingTag.id); // Set tag to wiggle
        setTimeout(() => setWiggleTag(null), 2000); // Remove wiggle after 2 seconds
        return; // Don't add if it's a duplicate
      }

      setTagLoading(true);
      try {
        const tag = await assetService.createTag(
          companyId,
          data.asset.ordSer_id,
          newTag
        );

        const newTags = [
          ...tags,
          {
            id: tag.tagId,
            tag: newTag,
          },
        ];

        setTags(newTags);
        dispatch(
          updateHardware({
            id: data.asset.ordSer_id,
            updatedData: { ...data.asset, tags: newTags },
          })
        );
        setInputValue('');
        setDuplicateTagError(null); // Clear error when tag is added

        // Refocus the input
        tagInput.current?.focus();
      } catch (error) {
        console.error('Error creating tag:', error);
      } finally {
        setTagLoading(false);
      }
    }
  };

  const removeTag = async (tagId: number) => {
    try {
      await assetService.deleteTag(companyId, data.asset.ordSer_id, tagId);
      const updatedTags = tags.filter((tag) => tag.id !== tagId);
      setTags(updatedTags);
      dispatch(
        updateHardware({
          id: data.asset.ordSer_id,
          updatedData: { ...data.asset, tags: updatedTags },
        })
      );
      tagInput.current?.focus();
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  };

  const handleRemoveUser = async () => {
    setLoading(true);
    try {
      const asset = await assetService.removeUser(
        companyId,
        data.asset.ordSer_id
      );

      const updatedHardware = {
        ...assetData,
        ordSer_user_id: null,
        ordSer_username: '',
      };

      setAssetData(updatedHardware);
      dispatch(
        updateHardware({
          id: data.asset.ordSer_id,
          updatedData: updatedHardware,
        })
      );

      // setAssetData(asset);
      forceUpdate();

      dispatch(
        addToast({
          description: 'Gebruiker verwijderd van het apparaat.',
          position: 'bottomRight',
          style: 'success',
        })
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error removing user from asset:', error);
    }
  };

  return (
    <div>
      <div>
        <AssetCategory category={data.asset.ordPro_categorie} />
        <TextHeader title={data.asset.ordPro_naam} />
      </div>
      {formLoading ? (
        <LoadingSpinner />
      ) : (
        <div>
          <div className="my-5">
            <SectionCard title={'Gebruikersinformatie'}>
              <div className="flex flex-col md:flex-row gap-8 ">
                {/* User Select with label and delete icon */}
                <div className="w-full md:w-1/3">
                  <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Gebruiker
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-grow">
                      <UserSelect
                        key={force}
                        handleSelect={handleSelect}
                        defaultValue={
                          assetData.ordSer_user_id ?? 'Selecteer gebruiker'
                        }
                        loading={loading}
                      />
                    </div>
                    <button
                      className="text-red-500 hover:text-red-700 focus:outline-none"
                      onClick={handleRemoveUser}
                      title="Gebruiker verwijderen"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <div className="w-full md:w-2/3">
                  <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tags
                  </label>
                  <div
                    className={`flex flex-wrap items-center border border-gray-300 dark:border-slate-600 dark:text-gray-300 text-sm rounded-lg px-2.5 py-1.5 bg-transparent dark:bg-gray-900 focus-within:outline focus-within:outline-blue-700 ${
                      tagLoading ? 'pr-10' : ''
                    }`}
                  >
                    {tags?.length > 0 &&
                      tags.map((tag, index) => (
                        <div
                          key={tag.id}
                          onClick={() => removeTag(tag.id)}
                          className={`flex items-center px-2 rounded-full text-xs mr-1 mb-1 cursor-pointer ${
                            wiggleTag === tag.id
                              ? 'animate-bounce text-white bg-red-300'
                              : 'bg-gray-100 dark:bg-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {tag.tag}
                          <span className="ml-1 text-red-500">&times;</span>
                        </div>
                      ))}
                    <input
                      className="flex-grow bg-transparent outline-none text-sm dark:placeholder-gray-400"
                      type="text"
                      placeholder={t('Voeg tags toe (druk op Enter)')}
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyPress}
                      disabled={tagLoading}
                      ref={tagInput}
                    />
                    {tagLoading && (
                      <div className="ml-2">
                        <LoadingSpinner size={18} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          <Tabs tabs={tabs} />
        </div>
      )}
    </div>
  );
};

export default AssetDetailContainer;
